import { catchError } from 'rxjs/operators';
// angular
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// addons
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, mergeMap, tap, withLatestFrom, filter } from 'rxjs/operators';
import { of } from 'rxjs';

// service
import { TradeService, OrganizationService } from '@core/services';

// store
import { Store, select } from '@ngrx/store';
import { State } from '@store/index';
import * as actions from './actions';
import * as feedActions from '../feed/actions';
import * as userSelectors from '../user/selectors';
import * as organizationSelectors from '../organization/selectors';
import * as tradesActions from '../trades/actions';
import * as uiActions from '../ui/actions';

// model
import {
  ITrade,
  TradeStatusesEnum,
  IOrganization,
  UserRolesEnum,
  IError,
  ToastContextEnum
} from '@shared/models';

@Injectable()
export class TradeEffects {
  loadTrade$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Load),
    mergeMap(({ id }: { id: number }) => {
      return this.tradeService.getTrade(id).pipe(
        map((trade: ITrade) => actions.loadSuccess({ trade }))
      );
    })
  ));

  loadFeedAfterTrade$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.LoadSuccess),
    withLatestFrom(this.store.pipe(select(userSelectors.getRole))),
    filter(([{ trade }, role]: [{ trade: ITrade }, string]) => role !== UserRolesEnum.Notary),
    map(([{ trade }, role]: [{ trade: ITrade }, string]) => feedActions.load())
  ));

  clearTrade$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Clear),
    map(() => feedActions.clear())
  ));

  cancelTrade$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Cancel),
    mergeMap(({ id }: { id: number }) => {
      return this.tradeService.cancelTrade(id).pipe(
        tap(() => this.router.navigate([], {
          queryParams: {
            status: TradeStatusesEnum.Cancelled
          },
          fragment: String(id)
        }))
      );
    })
  ), { dispatch: false });

  approveTrade$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Approve),
    mergeMap(({ id }: { id: number }) => {
      return this.tradeService.approveTrade(id).pipe(
        tap(() => this.router.navigate([], {
          queryParams: {
            status: TradeStatusesEnum.Payment
          },
          fragment: String(id)
        })),
        map(() => uiActions.openToast({ toast: {
          id: Math.random(),
          message: 'Successfully approved trade!',
          context: ToastContextEnum.Success
        }})),
        catchError((error: IError) => of(
          uiActions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  endTrade$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.End),
    mergeMap(({ id }: { id: number }) => {
      return this.tradeService.endTrade(id).pipe(
        tap(() => this.router.navigate([], {
          queryParams: {
            status: TradeStatusesEnum.Closed
          },
          fragment: String(id)
        }))
      );
    })
  ), { dispatch: false });

  declineTrade$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Decline),
    mergeMap(({ id }: { id: number }) => {
      return this.tradeService.declineTrade(id).pipe(
        map(() => tradesActions.loadPerOrganization({ status: null })),
        catchError((error: IError) => of(
          uiActions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  finalizeTrade$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Finalize),
    mergeMap(({ id, userIds }: { id: number, userIds: number[] }) => {
      return this.tradeService.finalizeTrade(id, userIds).pipe(
        map(() => {
          this.store.dispatch(uiActions.openToast({ toast: {
            id: Math.random(),
            message: 'Trade successfully finalized!',
            context: ToastContextEnum.Success
          }}));

          return tradesActions.loadPerOrganization({ status: null }); }
        ),
        catchError((error: IError) => of(
          uiActions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  removeTrade$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Remove),
    mergeMap(({ id }: { id: number }) => {
      return this.tradeService.removeTrade(id).pipe(
        map(() => tradesActions.loadPerOrganization({ status: null })),
        catchError((error: IError) => of(
          uiActions.openToast({ toast: {
              id: Math.random(),
              message: error.message,
              context: ToastContextEnum.Error
            }})
        ))
      );
    })
  ));

  constructor(
    private actions$: Actions,
    private tradeService: TradeService,
    private router: Router,
    private store: Store<State>
  ) {}
}
